<template>
  <div class="market-home">
  <nav aria-label="Breadcrumb" class="home-breadcrumb-bar">
    <ol class="home-breadcrumb-list">
      <li class="home-breadcrumb-item">
        <NuxtLink to="/" class="home-breadcrumb-link">خانه</NuxtLink>
      </li>
    </ol>
  </nav>

  <section class="hero" :style="heroStyle">
      <div class="hero-overlay"></div>
      <div class="hero-grain"></div>

      <div class="hero-inner">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          <span>{{ $t('home.slider_1_title') }}</span>
        </div>

        <h1 class="hero-headline">
          <span class="headline-main">{{ $t('home.slider_1_desc') }}</span>
          <span class="headline-accent">{{ $t('home.hero_accent') }}</span>
        </h1>
        <p class="hero-sub">{{ $t('home.hero_subtitle') }}</p>

        <div class="hero-actions">
          <NuxtLink :to="localePath('/products')" class="btn-primary">
            <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
              <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ $t('home.hero_cta_products') }}</span>
          </NuxtLink>
          <NuxtLink :to="localePath('/add-adv')" class="btn-ghost"><i class="fas fa-plus-circle"></i>{{ $t('home.banner_add_btn') }}</NuxtLink>
          <NuxtLink :to="localePath('/add-ord')" class="btn-ghost"><i class="fas fa-plus-circle"></i>{{ $t('home.banner_order_btn') }}</NuxtLink>
        </div>

        <!-- Floating search card -->
        <div class="hero-search-card">
          <SearchForm />
        </div>

        <div class="hero-stats">
          <div class="stat-cell">
            <span class="stat-dig">{{ $t('home.stat_farmers_count') }}</span>
            <span class="stat-lbl">{{ $t('home.stat_farmers') }}</span>
          </div>
          <div class="stat-divide"></div>
          <div class="stat-cell">
            <span class="stat-dig">{{ $t('home.stat_products_count') }}</span>
            <span class="stat-lbl">{{ $t('home.stat_products') }}</span>
          </div>
          <div class="stat-divide"></div>
          <div class="stat-cell">
            <span class="stat-dig">{{ $t('home.stat_provinces_count') }}</span>
            <span class="stat-lbl">{{ $t('home.stat_provinces') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TRUST STRIP ===== -->
    <section class="trust-strip">
      <div class="container-wide">
        <div class="trust-grid">
          <div class="trust-item">
            <div class="trust-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" width="22" height="22">
                <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="7" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/>
              </svg>
            </div>
            <div class="trust-text">
              <strong>{{ $t('home.trust_1_title') }}</strong>
              <span>{{ $t('home.trust_1_desc') }}</span>
            </div>
          </div>
          <div class="trust-item">
            <div class="trust-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" width="22" height="22">
                <rect x="4" y="10" width="16" height="10" rx="2" stroke-linecap="round"/>
                <path d="M8 10V7a4 4 0 018 0v3" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="trust-text">
              <strong>{{ $t('home.trust_2_title') }}</strong>
              <span>{{ $t('home.trust_2_desc') }}</span>
            </div>
          </div>
          <div class="trust-item">
            <div class="trust-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" width="22" height="22">
                <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="trust-text">
              <strong>{{ $t('home.trust_3_title') }}</strong>
              <span>{{ $t('home.trust_3_desc') }}</span>
            </div>
          </div>
          <div class="trust-item">
            <div class="trust-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" width="22" height="22">
                <path d="M4 12a8 8 0 0114-5M20 12a8 8 0 01-14 5" stroke-linecap="round"/>
                <path d="M18 3v4h-4M6 21v-4h4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="trust-text">
              <strong>{{ $t('home.trust_4_title') }}</strong>
              <span>{{ $t('home.trust_4_desc') }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CATEGORIES ===== -->
    <section class="section">
      <div class="container-wide">
        <div class="section-head">
          <div class="section-head-text">
            <span class="section-tag">{{ $t('home.categories_tag') }}</span>
            <h2 class="section-title"><span class="title-em">{{ $t('home.categories_title_em') }}</span> {{ $t('home.categories_title_after') }}</h2>
            <p class="section-desc">{{ $t('home.categories_desc') }}</p>
          </div>
        </div>
        <ProductCategories />
      </div>
    </section>

    <!-- ===== HOT PRODUCTS ===== -->
    <section class="section">
      <div class="container-wide">
        <div class="section-head section-head-row">
          <div class="section-head-text">
            <span class="section-tag">{{ $t('home.hot_tag') }}</span>
            <h2 class="section-title">{{ $t('home.hot_title_before') }} <span class="title-em">{{ $t('home.hot_title_em') }}</span> {{ $t('home.hot_title_after') }}</h2>
          </div>
          <NuxtLink :to="localePath('/products')" class="view-all">
            {{ $t('home.hot_view_all') }}
            <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
            </svg>
          </NuxtLink>
        </div>
        <div class="products-grid">
          <NuxtLink v-for="p in hotProducts" :key="p.id" :to="localePath(`/details/${p.invoiceNum}`)" class="product-card"  target="_blank">
            <div class="product-media">
              <NuxtImg :src="`https://sarvban.com/uploads/market/${p.userId}/${p.image}`" :alt="p.title" @error="handleImageError" />
              <span v-if="p.hot" class="product-badge">{{ $t('home.hot_badge') }}</span>
            </div>
            <div class="product-body">
              <h4 class="product-title">{{ p.title }}</h4>
              <span class="product-seller">{{ p.username }}</span>
              <div class="product-price">
                <span class="price-value">{{ formatPrice(p.price?.price) }}</span>
                <span class="price-unit">{{ p.price?.unit }}</span>
              </div>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <SarvbanJourneys ref="" />

    <!-- ===== FLASH SALE ===== -->
    <section class="section section-flash">
      <div class="container-wide">
        <FlashSale :products="requestProducts" />
      </div>
    </section>

    <!-- ===== CTA BANNER 1 ===== -->
    <section class="section">
      <div class="container-wide">
        <div class="banner banner-amber">
          <div class="banner-glow"></div>
          <div class="banner-content">
            <div class="banner-text">
              <span class="banner-tag">{{ $t('home.cta_1_text') }}</span>
              <h3 class="banner-title">{{ $t('home.banner_order_title') }}</h3>
            </div>
            <NuxtLink :to="localePath('/add-ord')" class="btn-dark">
              <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
              </svg>
              {{ $t('home.banner_order_btn') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== SELLER PRODUCTS ===== -->
    <section class="section section-sm">
      <div class="container-wide">
        <ProductList
          :products="sellerProducts"
          :title="$t('home.seller_products')"
          icon="fa-solid fa-calendar-days"
          col-class="col-4"
          :badge-text="$t('home.badge_special')"
          view-all-link="/products/seller"
        />
      </div>
    </section>

    <!-- ===== CTA BANNER 2 ===== -->
    <section class="section">
      <div class="container-wide">
        <div class="banner banner-olive">
          <div class="banner-glow"></div>
          <div class="banner-content">
            <div class="banner-text">
              <span class="banner-tag banner-tag-dark">{{ $t('home.cta_2_text') }}</span>
              <h3 class="banner-title banner-title-dark">{{ $t('home.cta_2_title') }}</h3>
            </div>
            <NuxtLink :to="localePath('/products')" class="btn-primary">
              <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
              </svg>
              {{ $t('home.cta_2_btn') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>


    <!-- ===== BENEFITS ===== -->
    <section class="section section-tint">
      <div class="container-wide">
        <div class="section-head">
          <div class="section-head-text">
            <span class="section-tag">{{ $t('home.benefits_tag') }}</span>
            <h2 class="section-title">{{ $t('home.benefits_title_before') }} <span class="title-em">{{ $t('home.benefits_title_em') }}</span> {{ $t('home.benefits_title_after') }}</h2>
            <p class="section-desc">{{ $t('home.benefits_desc') }}</p>
          </div>
        </div>
        <div class="benefits-grid">
          <div v-for="(ben, i) in benefitsList" :key="i" class="benefit-card" :style="{ '--d': i }">
            <div class="benefit-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="benefit-icon" v-html="ben.icon"></div>
            <h3 class="benefit-title">{{ ben.title }}</h3>
            <p class="benefit-desc">{{ ben.desc }}</p>
          </div>
        </div>
        <div class="banner banner-benefit">
          <div class="banner-glow"></div>
          <div class="banner-content">
            <div class="banner-text">
              <h3 class="banner-title">{{ $t('home.cta_4_title') }}</h3>
              <span class="banner-tag">{{ $t('home.cta_4_text') }}</span>
              <span class="banner-tag">{{ $t('home.cta_4_text_2') }}</span>
            </div>
            <NuxtLink :to="localePath('/about-sarvban')" class="btn-dark">
              <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
              </svg>
              {{ $t('home.hero_cta_about') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== BUYER / SELLER CTAs ===== -->
    <section class="section" v-if="false">
      <div class="container-wide">
        <div class="section-head">
          <div class="section-head-text">
            <h2 class="section-title">{{ $t('homeBuyerSeller.title') }}</h2>
          </div>
        </div>
        <target-audience/>
        <div class="role-grid">
          <div class="role-card role-card-seller">
            <div class="role-icon">
              <svg viewBox="0 0 48 48" fill="none" width="36" height="36">
                <rect x="10" y="16" width="28" height="24" rx="3" stroke="#f2643d" stroke-width="1.8" fill="none"/>
                <path d="M16 24h16M16 30h16M16 36h10" stroke="#f2643d" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M24 8l-6 8h12L24 8z" fill="#f2643d" fill-opacity="0.15" stroke="#f2643d" stroke-width="1.5"/>
              </svg>
            </div>
            <h3 class="role-title">{{ $t('homeBuyerSeller.seller_title') }}</h3>
            <p class="role-desc">{{ $t('homeBuyerSeller.seller_desc') }}</p>
            <NuxtLink :to="localePath('/add-adv')" class="role-btn role-btn-seller">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path d="M10 5v10M5 10h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              {{ $t('homeBuyerSeller.seller_btn') }}
            </NuxtLink>
          </div>
          <div class="role-card role-card-buyer">
            <div class="role-icon">
              <svg viewBox="0 0 48 48" fill="none" width="36" height="36">
                <circle cx="24" cy="24" r="16" stroke="#1a6b3c" stroke-width="1.8" fill="none"/>
                <path d="M24 16v16M16 24h16" stroke="#1a6b3c" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M32 32l6 6" stroke="#1a6b3c" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="role-title">{{ $t('homeBuyerSeller.buyer_title') }}</h3>
            <p class="role-desc">{{ $t('homeBuyerSeller.buyer_desc') }}</p>
            <NuxtLink :to="localePath('/add-ord')" class="role-btn role-btn-buyer">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path d="M10 5v10M5 10h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              {{ $t('homeBuyerSeller.buyer_btn') }}
            </NuxtLink>
          </div>
          <div class="role-card role-card-services">
            <div class="role-icon">
              <svg viewBox="0 0 48 48" fill="none" width="36" height="36">
                <circle cx="24" cy="24" r="16" stroke="#1a6b3c" stroke-width="1.8" fill="none"/>
                <path d="M24 16v16M16 24h16" stroke="#1a6b3c" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M32 32l6 6" stroke="#1a6b3c" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="role-title">{{ $t('homeBuyerSeller.services_title') }}</h3>
            <p class="role-desc">{{ $t('homeBuyerSeller.services_desc') }}</p>
            <NuxtLink :to="localePath('/add-ads')" class="role-btn role-btn-buyer">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path d="M10 5v10M5 10h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              {{ $t('homeBuyerSeller.services_btn') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== SERVICES ===== -->
    <section class="section section-sm">
      <div class="container-wide">
        <ServicesSection />
        <div class="banner banner-services">
          <div class="banner-glow"></div>
          <div class="banner-content">
            <div class="banner-text">
              <h3 class="banner-title">{{ $t('home.cta_5_title') }}</h3>
              <span class="banner-tag">{{ $t('home.cta_5_text') }}</span>
              <span class="banner-tag">{{ $t('home.cta_5_text_2') }}</span>
            </div>
            <NuxtLink :to="localePath('/about-services')" class="btn-dark">
              <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
              </svg>
              {{ $t('home.cta_5_cta') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PRE-SELL PRODUCTS ===== -->
    <section class="section section-sm">
      <div class="container-wide">
        <ProductList
          :products="preSellProducts"
          :title="$t('home.pre_sell_products')"
          icon="fa-solid fa-calendar-days"
          col-class="col-4"
          :badge-text="$t('home.badge_presale')"
          view-all-link="/products/outofseasion"
        />
      </div>
    </section>

    <!-- ===== TESTIMONIALS ===== -->
    <section class="section section-tint">
      <div class="container-wide">
        <div class="section-head">
          <div class="section-head-text">
            <span class="section-tag">{{ $t('home.testimonials_tag') }}</span>
            <h2 class="section-title">{{ $t('home.testimonials_title_before') }} <span class="title-em">{{ $t('home.testimonials_title_em') }}</span>{{ $t('home.testimonials_title_after') }}</h2>
          </div>
        </div>
        <div class="testimonial-grid">
          <div v-for="(t, i) in testimonialsList" :key="i" class="testimonial-card" :style="{ '--d': i }">
            <div class="quote-mark">"</div>
            <p class="quote-text">{{ t.text }}</p>
            <div class="quote-author">
              <div class="quote-avatar">{{ t.initials }}</div>
              <div>
                <strong>{{ t.name }}</strong>
                <span>{{ t.role }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== DISCOUNT CTA ===== -->
    <section class="section">
      <div class="container-wide">
        <div class="banner banner-seed">
          <div class="banner-glow"></div>
          <div class="banner-content banner-content-center">
            <div class="banner-text">
              <h3 class="banner-title">{{ $t('home.cta_3_title') }}</h3>
              <p class="banner-desc">{{ $t('home.cta_3_desc') }}</p>
            </div>
            <NuxtLink :to="localePath('/about-sarvban')" class="btn-dark">
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ $t('home.cta_3_btn') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TOP COLLECTIONS ===== -->
    <TopCollections />

    <!-- ===== HOW IT WORKS ===== -->
    <HowSarvbanWorks />

    <Footer></Footer>
  </div>
</template>

<script setup>
import ProductList from '@/components/ProductList.vue'

definePageMeta({ layout: 'default' })
const baseURL = useRuntimeConfig().app.baseURL
const siteUrl = useRuntimeConfig().public.baseUrl
const canonicalUrl = `${siteUrl}${baseURL}`

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { checkToken } = useTokenLogin()

// const breadcrumbItems = useBreadcrumbItems()

useSchemaOrg([
  defineOrganization({
    name: 'سروبان',
    url: 'https://sarvban.com',
    logo: 'https://sarvban.com/img/sarvban_logo_2.png',
    sameAs: ['https://sarvban.com']
  })
])

useSeoMeta({
  title: () => t('home.title'),
  description: () => t('home.description'),
  ogTitle: () => t('home.title'),
  ogDescription: () => t('home.description'),
  ogType: 'website',
  ogImage: () => `${siteUrl}${baseURL}img/slider/main-page.jpg`,
  ogUrl: () => canonicalUrl,
  ogSiteName: 'سروبان',
  twitterCard: 'summary_large_image',
  twitterTitle: () => t('home.title'),
  twitterDescription: () => t('home.description'),
  twitterImage: () => `${siteUrl}${baseURL}img/slider/main-page.jpg`
})

useHead({
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ]
})

const hotProducts = ref([])
const sellerProducts = ref([])
const requestProducts = ref([])
const preSellProducts = ref([])
const totalProducts = ref(null)

const { data } = await useFetch('/market/api/mobile/home', {
  key: 'home',
  default: () => ({
    count: 0, hotProducts: [], sellerProducts: [],
    requestProducts: [], preSellProducts: []
  })
})

const heroStyle = computed(() => ({
  backgroundImage: `linear-gradient(180deg, rgba(8,30,17,0.72) 0%, rgba(10,42,24,0.55) 45%, rgba(8,30,17,0.82) 100%), url(${baseURL}img/slider/main-page.jpg)`
}))

function handleImageError(event) {
  if (event.target) {
    event.target.src = `${siteUrl}/img/products/default.jpg`
  }
}
onMounted(() => {
  if (data.value) {
    hotProducts.value = data.value.hotProducts || []
    sellerProducts.value = data.value.sellerProducts || []
    requestProducts.value = data.value.requestProducts || []
    preSellProducts.value = data.value.preSellProducts || []
    if (data.value.count) {
      totalProducts.value = new Intl.NumberFormat('fa-IR').format(data.value.count)
    }
  }
})

// Auto login when the url has a token param, e.g. /market?token=...
onMounted(() => {
  const token = route.query.token
  if (!token) return

  checkToken(token).then(() => {
    // Remove token param from url so a refresh won't re-trigger the login
    if (process.client) {
      router.replace({ query: { ...route.query, token: undefined } })
    }
  })
})

const formatPrice = (price) => {
  if (!price) return '—'
  return new Intl.NumberFormat('fa-IR').format(price)
}

const categoryIcons = [
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><circle cx="24" cy="28" r="12" fill="#1a6b3c" fill-opacity="0.12" stroke="#1a6b3c" stroke-width="1.5"/><path d="M24 16C24 16 18 10 18 7c0-2 4-2 6 1 2-3 6-3 6-1 0 3-6 9-6 9z" fill="#1a6b3c" fill-opacity="0.2" stroke="#1a6b3c" stroke-width="1.2"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><circle cx="24" cy="22" r="11" fill="#d4893a" fill-opacity="0.1" stroke="#d4893a" stroke-width="1.5"/><path d="M24 12c0-3 4-6 8-6s6 2 5 5-5 4-8 4c-3 0-5-1-5-3z" fill="#d4893a" fill-opacity="0.15" stroke="#d4893a" stroke-width="1"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><rect x="16" y="10" width="16" height="28" rx="3" fill="#1a6b3c" fill-opacity="0.1" stroke="#1a6b3c" stroke-width="1.5"/><rect x="19" y="24" width="10" height="6" rx="1.5" fill="#1a6b3c" fill-opacity="0.12" stroke="#1a6b3c" stroke-width="1"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><ellipse cx="28" cy="28" rx="8" ry="6" fill="#1a6b3c" fill-opacity="0.1" stroke="#1a6b3c" stroke-width="1.5"/><circle cx="16" cy="34" r="5" fill="#d4893a" fill-opacity="0.08" stroke="#d4893a" stroke-width="1.2"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><path d="M24 8c0 6-6 14-6 20 0 4 3 6 6 6s6-2 6-6c0-6-6-14-6-20z" fill="#1a6b3c" fill-opacity="0.1" stroke="#1a6b3c" stroke-width="1.5"/><path d="M24 34v14" stroke="#1a6b3c" stroke-width="1.5"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><ellipse cx="24" cy="28" rx="11" ry="8" fill="#d4893a" fill-opacity="0.08" stroke="#d4893a" stroke-width="1.5"/><path d="M16 26c4-4 12-4 16 0" stroke="#1a6b3c" stroke-width="1.2" fill="none"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><ellipse cx="24" cy="28" rx="6" ry="8" fill="#1a6b3c" fill-opacity="0.1" stroke="#1a6b3c" stroke-width="1.5"/><ellipse cx="24" cy="28" rx="3" ry="5" fill="#1a6b3c" fill-opacity="0.15"/></svg>',
  '<svg viewBox="0 0 48 48" fill="none" width="28" height="28"><rect x="12" y="18" width="24" height="22" rx="2" fill="#1a6b3c" fill-opacity="0.08" stroke="#1a6b3c" stroke-width="1.5"/><path d="M24 6v12" stroke="#1a6b3c" stroke-width="1.5"/><path d="M16 14l4 4" stroke="#1a6b3c" stroke-width="1.2"/><path d="M32 14l-4 4" stroke="#1a6b3c" stroke-width="1.2"/></svg>',
]

const categoryList = computed(() => [
  { name: t('home.cat_1'), icon: categoryIcons[0] },
  { name: t('home.cat_2'), icon: categoryIcons[1] },
  { name: t('home.cat_3'), icon: categoryIcons[2] },
  { name: t('home.cat_4'), icon: categoryIcons[3] },
  { name: t('home.cat_5'), icon: categoryIcons[4] },
  { name: t('home.cat_6'), icon: categoryIcons[5] },
  { name: t('home.cat_7'), icon: categoryIcons[6] },
  { name: t('home.cat_8'), icon: categoryIcons[7] }
])

const benefitIcons = [
  '<svg viewBox="0 0 28 28" fill="none" stroke="#1a6b3c" stroke-width="1.5" width="28" height="28"><path d="M14 24s8-4 8-10V5l-8-3-8 3v9c0 6 8 10 8 10z" stroke-linecap="round"/><path d="M11 13l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  '<svg viewBox="0 0 28 28" fill="none" stroke="#1a6b3c" stroke-width="1.5" width="28" height="28"><path d="M20 18V8M14 18V4M8 18v-4" stroke-linecap="round"/><circle cx="20" cy="5" r="2"/><circle cx="14" cy="12" r="2"/><circle cx="8" cy="8" r="2"/></svg>',
  '<svg viewBox="0 0 28 28" fill="none" stroke="#1a6b3c" stroke-width="1.5" width="28" height="28"><path d="M10 6H8a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-2m-6 0a2 2 0 012-2h2a2 2 0 012 2m-6 0h6" stroke-linecap="round"/><path d="M10 16l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  '<svg viewBox="0 0 28 28" fill="none" stroke="#1a6b3c" stroke-width="1.5" width="28" height="28"><path d="M4 22h20M4 12h20M4 9l10-5 10 5M4 12v10M24 12v10" stroke-linecap="round"/></svg>',
]

const benefitsList = computed(() => [
  { title: t('home.ben_1_title'), desc: t('home.ben_1_desc'), icon: benefitIcons[0] },
  { title: t('home.ben_2_title'), desc: t('home.ben_2_desc'), icon: benefitIcons[1] },
  { title: t('home.ben_3_title'), desc: t('home.ben_3_desc'), icon: benefitIcons[2] },
  { title: t('home.ben_4_title'), desc: t('home.ben_4_desc'), icon: benefitIcons[3] }
])

const testimonialsList = computed(() => [
  { text: t('home.test_1_text'), initials: t('home.test_1_initials'), name: t('home.test_1_name'), role: t('home.test_1_role') },
  { text: t('home.test_2_text'), initials: t('home.test_2_initials'), name: t('home.test_2_name'), role: t('home.test_2_role') },
  { text: t('home.test_3_text'), initials: t('home.test_3_initials'), name: t('home.test_3_name'), role: t('home.test_3_role') },
  { text: t('home.test_4_text'), initials: t('home.test_4_initials'), name: t('home.test_4_name'), role: t('home.test_4_role') }
])
</script>

<style lang="scss" scoped>
/* ============================================================
   MODERN ORGANIC MARKETPLACE
   Green (#1a6b3c) dominant · Orange (#f2643d) accent
   Clean ecommerce layout · Persian RTL
============================================================ */
/* ===== BREADCRUMBS ===== */
.home-breadcrumb-bar {
  background: #fff;
  border-bottom: 1px solid #eef3ec;
}

.home-breadcrumb-list {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  margin: 0;
}

.home-breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
}

.home-breadcrumb-item:not(:last-child)::after {
  content: '/';
  color: var(--warm-muted);
}

.home-breadcrumb-link {
  color: $olive;
  text-decoration: none;
  font-weight: 500;
}

.home-breadcrumb-link:hover {
  color: $olive-dark;
  text-decoration: underline;
}

.home-breadcrumb-current {
  color: var(--warm-black);
  font-weight: 700;
}

.market-home {
  background: #f7faf6;
  color: var(--warm-black);
  font-family: IRANYekanXFaNum, system-ui, sans-serif;
  line-height: 1.7;
  overflow-x: hidden;
  margin-top:10px
}

.container-wide {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ===== HERO ===== */
.hero {
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px 90px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

@media (max-width: 768px) {
  .hero {
    min-height: 78vh;
    padding: 120px 16px 70px;
  }
  .container-wide {  padding: 0px;}
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 20%, rgba(212,137,58,0.10), transparent 60%);
}

.hero-grain {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 40px,
    rgba(255,255,255,0.02) 40px,
    rgba(255,255,255,0.02) 41px
  );
  pointer-events: none;
}

.hero-inner {
  position: relative;
  max-width: 860px;
  text-align: center;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 18px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 100px;
  color: #ffe3c2;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 26px;
  backdrop-filter: blur(6px);
}

.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--saffron);
  box-shadow: 0 0 0 4px rgba(242,100,61,0.25);
}

.hero-headline {
  margin: 0 0 16px;
}

.headline-main {
  display: block;
  font-size: 1.9rem;
  font-weight: 800;
  color: #f4faf2;
  letter-spacing: -0.5px;
  line-height: 1.25;
  text-shadow: 0 2px 20px rgba(0,0,0,0.3);
}

.headline-accent {
  display: inline-block;
  font-size: 2.9rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffd9a8, var(--saffron));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@media (max-width: 768px) {
  .headline-main, .headline-accent { font-size: 2rem; }
}
@media (max-width: 480px) {
  .headline-main, .headline-accent { font-size: 1.5rem; }
}

.hero-sub {
  font-size: 1rem;
  color: #cfe3c8;
  max-width: 600px;
  margin: 0 auto 30px;
  line-height: 1.8;
}

@media (max-width: 768px) { .hero-sub { font-size: 0.9rem; } }

.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 34px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 30px;
  background: linear-gradient(135deg, var(--saffron), #ff8a5c);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(242,100,61,0.35);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 32px rgba(242,100,61,0.45);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: rgba(255,255,255,0.08);
  color: #e2f0da;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s;
  border: 1px solid rgba(255,255,255,0.16);
  backdrop-filter: blur(4px);
}

.btn-ghost:hover {
  background: rgba(255,255,255,0.14);
  border-color: rgba(255,255,255,0.24);
  transform: translateY(-2px);
}

/* Floating search card */
.hero-search-card {
  max-width: 640px;
  margin: 0 auto 40px;
  border-radius: 16px;
  padding: 10px 14px;
}

.hero-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 36px;
  flex-wrap: wrap;
}

@media (max-width: 480px) { .hero-stats { gap: 20px; } }

.stat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-dig {
  font-size: 1.8rem;
  font-weight: 800;
  color: #f4faf2;
}

.stat-lbl {
  font-size: 0.8rem;
  color: #a9c8a0;
  font-weight: 500;
}

.stat-divide {
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.14);
}

/* ===== TRUST STRIP ===== */
.trust-strip {
  background: #fff;
  border-bottom: 1px solid #eef3ec;
  box-shadow: 0 4px 20px rgba(26,107,60,0.05);
  position: relative;
  z-index: 3;
}

.trust-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
}

@media (max-width: 992px) { .trust-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .trust-grid { grid-template-columns: 1fr; } }

.trust-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-inline-end: 1px solid #eef3ec;
}

.trust-item:last-child { border-inline-end: none; }

.trust-icon {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(26,107,60,0.08);
  color: $olive;
}

.trust-text {
  display: flex;
  flex-direction: column;
}

.trust-text strong {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--warm-black);
}

.trust-text span {
  font-size: 0.75rem;
  color: var(--warm-gray);
  min-height: 70px;
}

/* ===== SECTIONS ===== */
.section {
  padding: 72px 0;
}
.section-sm { padding: 40px 0; }
.section-tint { background: #f0f6ef; }
.section-flash { background: linear-gradient(180deg, #fff 0%, #fdf3e7 100%); }

@media (max-width: 768px) {
  .section { padding: 48px 0; }
  .section-sm { padding: 24px 0; }
}

.section-head {
  text-align: center;
  margin-bottom: 40px;
}

.section-head-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  text-align: right;
  gap: 16px;
}

.section-tag {
  display: inline-block;
  padding: 5px 16px;
  background: rgba(26,107,60,0.08);
  border-radius: 100px;
  color: $olive;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.section-title {
  font-size: 2rem;
  font-weight: 800;
  color: var(--warm-black);
  margin: 0 0 10px;
  line-height: 1.3;
}

.title-em { color: var(--saffron); }

.section-desc {
  font-size: 0.95rem;
  color: var(--warm-gray);
  margin: 0;
  max-width: 560px;
  margin-inline: auto;
}

.view-all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $olive;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.view-all:hover {
  background: rgba(26,107,60,0.06);
  color: $olive-dark;
}

@media (max-width: 480px) {
  .section-head-row { flex-direction: column; align-items: flex-start; }
  .section-title { font-size: 1.5rem; }
}

/* ===== BENEFITS ===== */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 992px) { .benefits-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .benefits-grid { grid-template-columns: 1fr; } }

.benefit-card {
  position: relative;
  background: #fff;
  border: 1px solid #e8f0e6;
  border-radius: 16px;
  padding: 30px 24px;
  transition: all 0.35s;
  cursor: default;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(26,107,60,0.05);
}

.benefit-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, $olive, var(--saffron));
  opacity: 0;
  transition: opacity 0.35s;
}

.benefit-card:hover::before { opacity: 1; }
.benefit-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(26,107,60,0.12);
  border-color: rgba(26,107,60,0.2);
}

.benefit-num {
  font-size: 0.75rem;
  font-weight: 800;
  color: rgba(242,100,61,0.4);
  letter-spacing: 2px;
  margin-bottom: 16px;
}

.benefit-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26,107,60,0.08);
  border-radius: 12px;
  margin-bottom: 18px;
  transition: all 0.3s;
}

.benefit-card:hover .benefit-icon {
  background: rgba(242,100,61,0.12);
  transform: scale(1.05);
}

.benefit-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--warm-black);
  margin: 0 0 8px;
}

.benefit-desc {
  font-size: 0.85rem;
  color: var(--warm-gray);
  margin: 0;
  line-height: 1.7;
}

/* ===== PRODUCTS GRID ===== */
.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 992px) { .products-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 640px) { .products-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; } }

.product-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 2px 12px rgba(26,107,60,0.06);
  transition: all 0.3s;
  border: 1px solid #eef3ec;
}

.product-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(26,107,60,0.14);
  border-color: rgba(26,107,60,0.25);
}

.product-media {
  position: relative;
  height: 170px;
  overflow: hidden;
  background: #f0f6ef;
}

.product-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}

.product-card:hover .product-media img { transform: scale(1.06); }

.product-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 12px;
  background: linear-gradient(135deg, #ff5a3c, var(--saffron));
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: 100px;
  box-shadow: 0 4px 12px rgba(242,100,61,0.3);
}

.product-body { padding: 16px; }

.product-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--warm-black);
  margin: 0 0 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-seller {
  font-size: 0.75rem;
  color: var(--warm-gray);
  display: block;
  margin-bottom: 10px;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-value {
  font-size: 0.95rem;
  font-weight: 800;
  color: $olive;
}

.price-unit {
  font-size: 0.7rem;
  color: var(--warm-muted);
}

/* ===== BANNERS ===== */
.banner {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
}

.banner-amber {
  background: linear-gradient(135deg, #c97a2a, var(--saffron), #ff9a6b);
  padding: 44px 40px;
}

.banner-olive {
  background: linear-gradient(135deg, #0d2b1a, $olive-dark, $olive);
  padding: 44px 40px;
}

.banner-seed {
  background: linear-gradient(135deg, $olive-dark, #1a6b3c, $olive);
  padding: 52px 40px;
}
.banner-benefit {
  background: linear-gradient(135deg, $olive-dark, #1a6b3c, $olive);
  padding: 52px 40px;
  margin-top: 40px;
}
.banner-services {
  background:linear-gradient(135deg, #12434d, #1a586b, #1a336b);
  padding: 52px 40px;
  margin-top: 40px;
}

.banner-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.12), transparent 50%);
}

.banner-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.banner-content-center {
  flex-direction: column;
  text-align: center;
}

.banner-tag {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.75);
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
}

.banner-tag-dark { color: rgba(255,255,255,0.6); }

.banner-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
}

.banner-title-dark { color: #f0f5ec; }

.banner-desc {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.75);
  margin: 10px 0 0;
  max-width: 500px;
}

.btn-dark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 26px;
  background: rgba(0,0,0,0.22);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s;
  border: 1px solid rgba(255,255,255,0.18);
  white-space: nowrap;
  flex-shrink: 0;
  backdrop-filter: blur(4px);
}

.btn-dark:hover {
  background: rgba(0,0,0,0.35);
  transform: translateY(-2px);
}

@media (max-width: 480px) {
  .banner-content { flex-direction: column; text-align: center; }
  .banner-amber, .banner-olive, .banner-seed { padding: 30px 22px; }
  .banner-title { font-size: 1.2rem; }
}

/* ===== TESTIMONIALS ===== */
.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 992px) { .testimonial-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .testimonial-grid { grid-template-columns: 1fr; } }

.testimonial-card {
  background: #fff;
  border: 1px solid #e8f0e6;
  border-radius: 16px;
  padding: 28px 24px;
  transition: all 0.3s;
  cursor: default;
  box-shadow: 0 2px 12px rgba(26,107,60,0.05);
}

.testimonial-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(26,107,60,0.1);
  border-color: rgba(242,100,61,0.2);
}

.quote-mark {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--saffron);
  opacity: 0.3;
  line-height: 1;
  margin-bottom: -8px;
}

.quote-text {
  font-size: 0.88rem;
  color: var(--warm-gray);
  line-height: 1.8;
  margin: 0 0 20px;
}

.quote-author {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quote-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--saffron), $olive);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.quote-author strong {
  display: block;
  font-size: 0.82rem;
  color: var(--warm-black);
}

.quote-author span {
  font-size: 0.72rem;
  color: var(--warm-muted);
}

/* ===== BUYER/SELLER ROLE CTAs ===== */
.role-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
  max-width: 960px;
  margin: 0 auto;
}

@media (max-width: 640px) { .role-grid { grid-template-columns: 1fr; } }

.role-card {
  position: relative;
  border-radius: 18px;
  padding: 38px 28px;
  text-align: center;
  transition: all 0.35s;
  overflow: hidden;
}

.role-card:hover { transform: translateY(-6px); }

.role-card-seller {
  background: linear-gradient(160deg, #fff6f0, #fff0e8, #fff6f0);
  border: 1px solid rgba(242,100,61,0.15);
}
.role-card-seller:hover {
  border-color: rgba(242,100,61,0.3);
  box-shadow: 0 14px 36px rgba(242,100,61,0.14);
}

.role-card-buyer {
  background: linear-gradient(160deg, #f0f8f2, #e8f5e9, #f0f8f2);
  border: 1px solid rgba(26,107,60,0.15);
}
.role-card-buyer:hover {
  border-color: rgba(26, 48, 107, 0.3);
  box-shadow: 0 14px 36px rgba(26,107,60,0.14);
}
.role-card-services {
  background: linear-gradient(160deg, #f0f5f8, #e8ecf5, #f0f2f8);
  border: 1px solid rgba(26, 71, 107, 0.15);
}
.role-card-services:hover {
  border-color: rgba(26, 71, 107, 0.3);
  box-shadow: 0 14px 36px rgba(26, 71, 107, 0.14);
}

.role-icon {
  width: 66px;
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  border-radius: 16px;
}
.role-card-seller .role-icon { background: rgba(242,100,61,0.08); }
.role-card-buyer .role-icon { background: rgba(26,107,60,0.08); }

.role-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--warm-black);
  margin: 0 0 8px;
}

.role-desc {
  font-size: 0.85rem;
  color: var(--warm-gray);
  margin: 0 0 22px;
  line-height: 1.7;
  max-width: 300px;
  margin-inline: auto;
}

.role-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 30px;
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s;
}

.role-btn-seller {
  background: linear-gradient(135deg, var(--saffron), #ff8a5c);
  color: #fff;
  box-shadow: 0 4px 16px rgba(242,100,61,0.25);
}
.role-btn-seller:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(242,100,61,0.35); }

.role-btn-buyer {
  background: linear-gradient(135deg, $olive-dark, $olive);
  color: #fff;
  box-shadow: 0 4px 16px rgba(26,107,60,0.25);
}
.role-btn-buyer:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,107,60,0.35); }

/* ===== RESPONSIVE EXTRAS ===== */
@media (max-width: 480px) {
  .hero-actions { flex-direction: column; width: 100%; }
  .btn-primary, .btn-ghost { width: 100%; justify-content: center; }
  .stat-dig { font-size: 1.3rem; }
}

/* prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
